import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StyledCheckbox, StyledInput, Switch } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { UserAvatar } from 'components/User';
import React, { useCallback, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import useSWR from 'swr';

interface TrainerSetting {
    id: number;
    notify: boolean;
    visible: boolean;
}

interface Props {
    name: string;
    clubSlug?: string;
}

/**
 * Trainer section for the event form. Renders every available trainer as a table
 * row: a checkbox selects the trainer, and two toggles configure whether that
 * trainer receives booking notifications and whether they are shown on the public
 * event page. Toggles are disabled until the trainer is selected. A search box
 * filters the rows by name. The `trainers` form field holds only the selected
 * trainers as `{ id, notify, visible }`.
 */
export const EventTrainerSelection: React.FunctionComponent<Props> = ({ name, clubSlug: _clubSlug }) => {
    const clubSlug = useClubSlug(_clubSlug);
    const { control } = useFormContext();
    const {
        field: { onChange, value }
    } = useController({ control, name });
    const { data, isLoading } = useSWR(`club/${clubSlug}/relatives/list`);
    const [search, setSearch] = useState('');

    const trainers: TrainerSetting[] = value || [];

    // Return the selected-trainer entry for a user id, or undefined when not selected.
    const findTrainer = useCallback((id: number) => trainers.find((trainer) => trainer.id === id), [trainers]);

    // Add a trainer with defaults on select, or remove it entirely on deselect.
    const toggle = useCallback(
        (id: number) => {
            if (findTrainer(id)) {
                onChange(trainers.filter((trainer) => trainer.id !== id));
            } else {
                onChange([...trainers, { id, notify: true, visible: true }]);
            }
        },
        [trainers, onChange, findTrainer]
    );

    // Update the notify/visible flag of an already-selected trainer.
    const update = useCallback(
        (id: number, key: 'notify' | 'visible', next: boolean) => {
            onChange(trainers.map((trainer) => (trainer.id === id ? { ...trainer, [key]: next } : trainer)));
        },
        [trainers, onChange]
    );

    const users = data?.data ?? [];

    // Filter the visible rows by name; selection state is never affected.
    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return users;
        return users.filter((user) => user.name?.toLowerCase().includes(query));
    }, [users, search]);

    if (isLoading) return <>Lade ...</>;

    if (users.length === 0) {
        return <p className="tw-text-accents-6">Es sind keine Trainer verfügbar.</p>;
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <StyledInput
                bordered
                fullWidth
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder="Trainer suchen ..."
                aria-label="Trainer suchen"
                contentLeft={<FontAwesomeIcon icon={faSearch} className="tw-text-accents-4" />}
            />

            <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-accents-2">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-border-collapse tw-text-sm">
                        <thead>
                            <tr className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-accents-2 tw-bg-accents-1 tw-text-left *:tw-p-2">
                                <th className="tw-w-px" />
                                <th>Name</th>
                                <th>Benachrichtigen</th>
                                <th>Auf Buchungsseite sichtbar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => {
                                const trainer = findTrainer(user.id);
                                const checked = Boolean(trainer);

                                return (
                                    <tr
                                        key={user.id}
                                        onClick={() => toggle(user.id)}
                                        className="tw-cursor-pointer tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-accents-2 last:tw-border-b-0 hover:tw-bg-accents-1"
                                    >
                                        <td className="tw-w-px tw-p-3">
                                            {/* Wrap only the control so its own onChange toggles once; empty cell area still bubbles to the row. */}
                                            <span
                                                className="tw-inline-flex"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <StyledCheckbox isSelected={checked} onChange={() => toggle(user.id)} />
                                            </span>
                                        </td>
                                        <td className="tw-p-3">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <div className="tw-w-9 tw-shrink-0">
                                                    <UserAvatar size={40} user={user} />
                                                </div>
                                                <span className="tw-font-semibold">{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="tw-p-3">
                                            {/* Stop propagation on the switch only, so its white cell area still toggles the row. */}
                                            <span
                                                className="tw-inline-flex"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <Switch
                                                    checked={trainer ? trainer.notify : true}
                                                    disabled={!checked}
                                                    onChange={(next) => update(user.id, 'notify', next)}
                                                />
                                            </span>
                                        </td>
                                        <td className="tw-p-3">
                                            <span
                                                className="tw-inline-flex"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <Switch
                                                    checked={trainer ? trainer.visible : true}
                                                    disabled={!checked}
                                                    onChange={(next) => update(user.id, 'visible', next)}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default EventTrainerSelection;
